import { useEffect, useMemo, useState } from "react";
import { MAX_IMAGES } from "../../config/config";
import { configManager } from "../../data/managers/ConfigManager";
import {
  AISettings,
  AllConfigDataModel,
  WSSettings,
} from "../../data/models/Config/ConfigModel";
import {
  StorageImage,
  useStorageImages,
} from "../../data/providers/StorageProvider";

function createAISettings(settings?: AISettings) {
  return settings ? new AISettings(settings) : undefined;
}

function createWSSettings(settings?: WSSettings) {
  return settings ? new WSSettings(settings) : undefined;
}

const LOCKED_AI_FIELDS = new Set<keyof AISettings>(['companyId', 'idMapos']);
const LOCKED_WS_FIELDS = new Set<keyof WSSettings>([
  'accessToken',
  'phoneNumberId',
  'whatsappId',
]);

function hasStoredValue(value?: string) {
  return typeof value === 'string' && value.trim() !== '';
}

export function useSettings() {
  const [tabIndex, setTabIndex] = useState(0);

  const [aiSettings, setAISettings] = useState<AISettings | undefined>();
  const [lastAISettings, setLastAISettings] = useState<AISettings | undefined>();
  const [wsApiSettings, setWsApiSettings] = useState<WSSettings | undefined>();
  const [lastWsApiSettings, setLastWsApiSettings] = useState<
    WSSettings | undefined
  >();
  const [allConfig, setAllConfig] = useState<AllConfigDataModel | undefined>();
  const [lastAllConfig, setLastAllConfig] = useState<AllConfigDataModel | undefined>();

  const [dialogAISettingsOpen, setDialogAISettingsOpen] = useState(false);
  const [dialogWsApiSettingsOpen, setDialogWsApiSettingsOpen] = useState(false);
  const [dialogSyncSettingsOpen, setDialogSyncSettingsOpen] = useState(false);

  const [loadingAISettings, setLoadingAISettings] = useState(false);
  const [loadingWsAPISettings, setLoadingWsAPISettings] = useState(false);
  const [loadingSyncSettings, setLoadingSyncSettings] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );

  const companyId = aiSettings?.companyId;

  const {
    images: storageImages,
    loading,
    error,
    progress,
    uploadImages,
    deleteImage,
    refresh,
  } = useStorageImages(companyId);

  useEffect(() => {
    getAllData();
  }, []);

  const configuredMenuUrls = useMemo(
    () =>
      Array.from(
        new Set(
          (aiSettings?.urlsMenu || [])
            .map((url) => String(url).trim())
            .filter(Boolean),
        ),
      ),
    [aiSettings?.urlsMenu],
  );

  const allMenuUrls = useMemo(
    () =>
      Array.from(
        new Set([
          ...configuredMenuUrls,
          ...storageImages.map((image) => image.url),
        ]),
      ),
    [configuredMenuUrls, storageImages],
  );

  const images = useMemo(() => {
    const storageUrls = new Set(storageImages.map((image) => image.url));
    const configuredImages = configuredMenuUrls
      .filter((url) => !storageUrls.has(url))
      .map((url, index) => ({
        name: `carta-configurada-${index + 1}`,
        url,
        fullPath: "",
        source: "configured" as const,
      }));

    return [...storageImages, ...configuredImages];
  }, [configuredMenuUrls, storageImages]);

  useEffect(() => {
    // No reemplazar las URLs configuradas mientras Storage aún está vacío o
    // cargando. Las cartas antiguas pueden existir solo en urlsMenu.
    if (!aiSettings || loading || storageImages.length === 0) return;

    const mergedUrls = allMenuUrls;
    if (
      mergedUrls.length === configuredMenuUrls.length &&
      mergedUrls.every((url, index) => url === configuredMenuUrls[index])
    ) {
      return;
    }

    updateUrlsMenu(mergedUrls);
    // updateUrlsMenu se mantiene estable para este efecto en la práctica; se
    // omite para no disparar una sincronización adicional en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSettings, allMenuUrls, configuredMenuUrls, loading, storageImages]);

  const handleAddImages = async (files: File[]) => {
    const filesAllowed = Math.min(MAX_IMAGES - images.length, files.length);
    if (filesAllowed <= 0) return;
    const uploadedImages = await uploadImages(files.slice(0, filesAllowed));
    if (uploadedImages.length > 0) {
      await updateUrlsMenu(
        Array.from(
          new Set([
            ...allMenuUrls,
            ...uploadedImages.map((image) => image.url),
          ]),
        ),
      );
    }
  };

  const handleRemoveImage = async (img: StorageImage) => {
    if (img.source === "configured") {
      await updateUrlsMenu(
        allMenuUrls.filter((url) => url !== img.url),
      );
      return;
    }

    const deleted = await deleteImage(img.fullPath);
    if (deleted) {
      await updateUrlsMenu(
        allMenuUrls.filter((url) => url !== img.url),
      );
    }
  };

  async function updateUrlsMenu(urls: string[]) {
    if (!aiSettings) return;
    setLoadingAISettings(true);
    try {
      const nextSettings = createAISettings({
        ...aiSettings,
        urlsMenu: urls,
      } as AISettings);
      const result = await configManager.updateConfigInfo(nextSettings!);
      if (result) {
        updateAiSettings(result.aiSettings);
        setDialogAISettingsOpen(false);
        setSnackbarMessage("Configuración AI actualizada con éxito");
        setSnackbarSeverity("success");
      } else {
        throw new Error("Error al actualizar la configuración AI");
      }
    } catch (error) {
      setSnackbarMessage("Error al actualizar la configuración AI");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
      setLoadingAISettings(false);
    }
  }

  async function getAllData() {
    const data = await configManager.getAllConfig();
    setAllConfig(data);
    setLastAllConfig(data ? new AllConfigDataModel(data) : undefined);
    const nextAISettings = createAISettings(data?.aiSettings);
    const nextWsSettings = createWSSettings(data?.wsSettings);
    setAISettings(nextAISettings);
    setLastAISettings(createAISettings(nextAISettings));
    setWsApiSettings(nextWsSettings);
    setLastWsApiSettings(createWSSettings(nextWsSettings));
  }

  function changeTab(_event: React.SyntheticEvent, newIndex: number) {
    setTabIndex(newIndex);
  }

  function openAISettingsDialog() {
    setDialogAISettingsOpen(true);
  }

  async function confirmUpdateAiSettings() {
    setLoadingAISettings(true);
    try {
      const nextSettings = createAISettings(aiSettings);
      const result = await configManager.updateConfigInfo(nextSettings!);
      if (result) {
        updateAiSettings(result.aiSettings);
        setDialogAISettingsOpen(false);
        setSnackbarMessage("Configuración AI actualizada con éxito");
        setSnackbarSeverity("success");
      } else {
        throw new Error("Error al actualizar la configuración AI");
      }
    } catch (error) {
      setSnackbarMessage("Error al actualizar la configuración AI");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
      setLoadingAISettings(false);
    }
  }

  async function updateAiSettings(settings: AISettings | undefined) {
    const nextSettings = createAISettings(settings);
    setAISettings(nextSettings);
    setLastAISettings(createAISettings(nextSettings));
  }

  function openWsAPISettingsDialog() {
    setDialogWsApiSettingsOpen(true);
  }

  async function confirmUpdateWsAPISettings() {
    setLoadingWsAPISettings(true);
    try {
      const nextSettings = createWSSettings(wsApiSettings);
      const result = await configManager.updateConfigInfo(nextSettings!);
      if (result) {
        updateWsSettings(result.wsSettings);
        setDialogWsApiSettingsOpen(false);
        setSnackbarMessage(
          "Configuración de WhatsApp API actualizada con éxito",
        );
        setSnackbarSeverity("success");
      } else {
        throw new Error("Error al actualizar la configuración de WhatsApp API");
      }
    } catch (error) {
      setSnackbarMessage("Error al actualizar la configuración de WhatsApp API");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
      setLoadingWsAPISettings(false);
    }
  }

  async function updateWsSettings(settings: WSSettings | undefined) {
    const nextSettings = createWSSettings(settings);
    setWsApiSettings(nextSettings);
    setLastWsApiSettings(createWSSettings(nextSettings));
  }

  function openSyncDialog() {
    setDialogSyncSettingsOpen(true);
  }

  async function confirmSyncSettings() {
    setLoadingSyncSettings(true);
    try {
      const result = await configManager.syncProducts();
      if (result) {
        setDialogSyncSettingsOpen(false);
        setSnackbarMessage(
          result.skipped
            ? "El catálogo ya estaba actualizado para esta compañia."
            : `Catálogo actualizado: ${result.categoriesSynced} categorias, ${result.productsSynced} productos y ${result.embeddingsSynced} elementos procesados.`,
        );
        setSnackbarSeverity("success");
      } else {
        throw new Error("No se pudo completar la actualización del catálogo");
      }
    } catch (error) {
      setSnackbarMessage("Error al actualizar el catálogo desde MAPOS");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
      setLoadingSyncSettings(false);
    }
  }

  const hasAISettingsChanges =
    aiSettings &&
    lastAISettings &&
    JSON.stringify(aiSettings) !== JSON.stringify(lastAISettings);

  const hasWsSettingsChanges =
    wsApiSettings &&
    lastWsApiSettings &&
    JSON.stringify(wsApiSettings) !== JSON.stringify(lastWsApiSettings);

  const aiSettingsFieldChange = (key: keyof AISettings, value: string | any) => {
    if (aiSettings) {
      if (
        LOCKED_AI_FIELDS.has(key) &&
        hasStoredValue(lastAISettings?.[key] as string | undefined)
      ) {
        return;
      }

      setAISettings(
        createAISettings({ ...aiSettings, [key]: value } as AISettings),
      );
    }
  };

  const wsSettingsFieldChange = (key: keyof WSSettings, value: string) => {
    if (wsApiSettings) {
      if (
        LOCKED_WS_FIELDS.has(key) &&
        hasStoredValue(lastWsApiSettings?.[key] as string | undefined)
      ) {
        return;
      }

      setWsApiSettings(
        createWSSettings({ ...wsApiSettings, [key]: value } as WSSettings),
      );
    }
  };

  return {
    companyId,
    tabIndex,
    setTabIndex,
    changeTab,
    aiSettings,
    setAISettings,
    lastAISettings,
    dialogAISettingsOpen,
    setDialogAISettingsOpen,
    loadingAISettings,
    openAISettingsDialog,
    confirmUpdateAiSettings,
    aiSettingsFieldChange,
    hasAISettingsChanges,
    wsApiSettings,
    setWsApiSettings,
    lastWsApiSettings,
    dialogWsApiSettingsOpen,
    setDialogWsApiSettingsOpen,
    loadingWsAPISettings,
    openWsAPISettingsDialog,
    confirmUpdateWsAPISettings,
    wsSettingsFieldChange,
    hasWsSettingsChanges,
    allConfig,
    lastAllConfig,
    dialogSyncSettingsOpen,
    setDialogSyncSettingsOpen,
    loadingSyncSettings,
    openSyncDialog,
    confirmSyncSettings,
    refreshAllData: getAllData,
    images,
    loadingImages: loading,
    errorImages: error,
    progress,
    uploadImages: handleAddImages,
    deleteImage: handleRemoveImage,
    refreshImages: refresh,
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    setSnackbarMessage,
    snackbarSeverity,
    setSnackbarSeverity,
  };
}
